import React from 'react';
import { Link } from 'react-router-dom';

export type ErrorWithStatus = {
  status?: number;
  data?: { message?: string };
};

type NotificationFunctions = {
  error: (message: string | React.ReactElement) => void;
  warning: (message: string | React.ReactElement) => void;
};

/**
 * Handles errors from call creation API requests
 * @param error - The error object from the API request
 * @param notification - The notification functions from useNotification hook
 * @param options - Additional options for error handling
 * @param options.showDetailedConcurrentLimitMessage - Whether to show detailed message with links for concurrent call limit errors
 * @param options.defaultErrorMessage - Custom default error message prefix (default: 'Failed to start call: ')
 */
export const handleCallCreationError = (
  error: unknown,
  notification: NotificationFunctions,
  options: {
    showDetailedConcurrentLimitMessage?: boolean;
    defaultErrorMessage?: string;
  } = {},
): void => {
  const {
    showDetailedConcurrentLimitMessage = true,
    defaultErrorMessage = 'Failed to start call: ',
  } = options;

  const err = error as ErrorWithStatus;
  const errorMessage = err.data?.message;
  const status = err?.status;

  // Handle 403 permission denied (e.g. Member trying to create a call)
  if (status === 403) {
    notification.error(
      errorMessage ||
        'You don’t have permission to create calls. Contact your organization admin if you need this access.',
    );
    return;
  }

  // Handle 429 rate limit errors
  if (err?.status === 429 && errorMessage) {
    if (errorMessage.includes('Concurrent call limit')) {
      if (showDetailedConcurrentLimitMessage) {
        // Show detailed message with links to settings and billing
        notification.warning(
          <p>
            {errorMessage}. You can adjust by going to{' '}
            <Link to="/settings">
              <span className="text-neutral-0 font-bold underline">
                settings
              </span>
            </Link>{' '}
            or upgrade your{' '}
            <Link to="/billing">
              <span className="text-neutral-0 font-bold underline">plan</span>
            </Link>{' '}
            to increase your limit .
          </p>,
        );
      } else {
        // Show simple warning message
        notification.warning(
          "You have reached the account's concurrent call limit. Please upgrade your plan to increase your limit.",
        );
      }
    } else {
      // Other 429 errors
      notification.warning(errorMessage);
    }
    return;
  }

  // Handle 400 errors, now just regarding "international permissions" twilio error
  if (err?.status === 400 && errorMessage) {
    if (errorMessage.includes('international permissions')) {
      notification.warning(
        "Please contact support@fluents.ai if you'd like to enable outbound calls to this country for your account.",
      );
      return;
    }
  }

  // Handle other errors with messages
  if (errorMessage) {
    notification.error(errorMessage);
    return;
  }

  // Fallback for errors without messages
  notification.error(
    defaultErrorMessage +
      (error instanceof Error ? error.message : 'Unknown error'),
  );
};
